import { PostReport, User } from "./types";
import { T } from "react-translator-component"

export const isIOS = (client_id: string) => {
  return client_id === "CL002" || client_id === "CL016";
};

export const isAndroid = (client_id: string) => {
  return client_id === "CL001" || client_id === "CL015";
};

export const responsive = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 2,
  },
};

export const displayUserName = (name?: string) => {
  return name ?? `${T("no_name")}`
}

export const displayUserNickname = (nickname?: string) => {
  return nickname ?? `${T("no_nickname")}`
}

export const getChatUserName = (users: User[], currentUserId: number) => {
  // Step 1: Filter out the current user
  const filteredUsers = users.length > 2 ? users.filter((user) => user.id !== currentUserId) : users;

  // Step 2: Ensure at least 3 users are returned
  const selectedUsers = filteredUsers.slice(0, 3);

  // Step 3: Join the names of the selected users into a string
  const userNames = selectedUsers.map((user) => user.nickname).join(", ");

  // Step 4: Calculate the remaining count of users excluding the current user
  const remainingCount = filteredUsers.length;

  // Step 5: Return the string in the desired format
  return `${userNames} ${remainingCount > 3 ? "+ " + remainingCount : ""}`;
};

export const getRole = (userRole: string) => {
  let role = userRole?.toLocaleLowerCase();
  if (role === "member") {
    return `${T("member_role")}`;
  } else if (role === "leader") {
    return `${T("leader")}`;
  } else if (role === "sub-leader") {
    return `${T("sub_leader")}`;
  } else if (role === "finance") {
    return `${T("finance")}`;
  } else if (role === "waiting-approval") {
    return `${T("waiting_approval")}`;
  }else if (role === "payment-pending") {
    return `${T("payment_pending")}`;
  }else if (role === "denied") {
    return `${T("denied")}`;
  }
  return "";
};

export const isOver18YearsOld = (birthday: string) => {
  if (!birthday) return false

  const today = new Date()
  const birthDate = new Date(birthday);
  if (isNaN(birthDate.getTime())) return false

  const adultDate = new Date(
    birthDate.getFullYear() + 18,
    birthDate.getMonth(),
    birthDate.getDate()
  )

  return adultDate <= today
};

export const isRegularAppUser = (user: any) => {
  return user?.source != "admin" && user?.is_facilitator == 0 && user?.id_kind == "1"
};

export const isTempAppUserType2 = (user: any) => {
  return user?.source != "admin" && user?.is_facilitator == 0 && user?.id_kind == "2"
};

export const isTempAppUserType3 = (user: any) => {
  return user?.source != "admin" && user?.is_facilitator == 0 && user?.id_kind == "3"
};

export const isMemberRole = (userRole: string | undefined) => {
  if (!userRole) return false
  return userRole.toLocaleLowerCase() === 'member'
};

export const isFinanceRole = (userRole: string | undefined) => {
  if (!userRole) return false
  return userRole.toLocaleLowerCase() === 'finance'
};

export const isSubLeaderRole = (userRole: string | undefined) => {
  if (!userRole) return false
  return userRole.toLocaleLowerCase() === 'sub-leader'
};

export const isGroupLeader = (groupInfo: any, user: any) => {
  if (!groupInfo?.owner?.id || !user?.id) return false

  return groupInfo.owner.id === user.id
};

export const convertUserImage = (userImage: string) => {
  if (!userImage) return ""
  return `data:image/jpeg;base64, ${userImage}`
};

export const isClosedGroup = (closedAt: string) => {
  return closedAt ? true : false
}

export const isPrivateGroup = (groupIsPublic: string) => {
  if (!groupIsPublic) return false

  return groupIsPublic.toLocaleLowerCase() === "private"
};

export const HYPHEN_REGEX_TEXT = "-˗ᅳ᭸‐‑‒–—―⁃⁻−▬─━➖ーㅡ﹘﹣－ｰ𐄐 "

export const formatNumberHalfWidth = (v: string) => {
  return v.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 65248));
}

export const formatHyphenHalfWidth = (v: string) => {
  const regReplaceHyphen = new RegExp(`[${HYPHEN_REGEX_TEXT}]`, 'g')
  return v.replace(regReplaceHyphen, '-')
}

export const validInputNumber = (value: string) => {
  if (!value) return { valid: true, error: '' }

  const result = /^[0-9]+$/.test(value)
  return {
    valid: result,
    error: result ? '' : `${T('only_number_can_be_inputted')}`
  }
};

export const deleteHyphen = (v: string) => {
  const hyphenReg = new RegExp(`[${HYPHEN_REGEX_TEXT}]`, `g`)
  return v.replace(hyphenReg, '');
};

export const isPrivateChat = (chatType: string) => {
  if (!chatType) return false
  return chatType === "one_to_one"
};

// chat_typeがone_to_manyであってもチャット参加者が3人未満になったときに
// one_to_oneに更新されないため、参加者数で判定する
export const isMultiPersonChat = (chatType: string, chatUsers: any[]) => {
  if (!chatType || isGroupChat(chatType)) return false
  return chatUsers.length > 2
};

export const isGroupChat = (chatType: string) => {
  if (!chatType) return false
  return chatType === "group"
};

export const getPostCreatorName = (post: any) => {
  const isAnonymous = post.is_anonymous
  const creator = post?.creator
  let name = ""
  switch (isAnonymous) {
    case 3:
      name = `${T("anonymous")}`
      break
    case 2:
      name = displayUserName(creator?.name)
      break;
    default:
      name = displayUserNickname(creator?.nickname)
  }
  return name
};

export const isPostReported = (postReports: PostReport[], postId: number) => {
  if (!postReports || postReports.length === 0) return false

  return postReports.some((postReport: any) => postReport.post_id === postId)
};

export const findBlockData = (blockList: any[], targetId: number, blockType: "User" | "MemberGroup" = "User") => {
  if (!targetId || !blockList || blockList.length === 0) return

  return blockList.find((block: any) =>
    block.blocked_id === targetId && block.blocked_type === blockType
  )
};

// 投稿者とユーザーオブジェクトの比較
export const isPostCreator = (post: any, user: any) => {
  if (!post?.creator_id || !post?.creator_type) {
    return false
  }

  return post.creator_type === user?.class_name && post.creator_id === user?.id
}
